import { Textarea } from "@/components/ui/textarea";
import PrecedentsCard from "@/components/PrecedentsCard";
import StrategicList from "@/components/engagement/StrategicList";

/**
 * Overview tab — scope summary, objectives, risks, plus the possible-precedents card.
 *
 * Iter 85.62 · Objectives and Key Risks now render as StrategicList
 * components (one item per row, inline edit, hover-to-remove, restrained
 * add control) instead of a single multiline textarea per field. The
 * canonical string[] shape on the Engagement is unchanged — every
 * mutation still writes the full array via `onSave({ objectives: [...] })`.
 * Scope summary keeps its textarea because it's a paragraph, not a list.
 */
export default function EngagementOverviewTab({ engagement: e, engagementId, onSave }) {
  return (
    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
      <PrecedentsCard engagementId={engagementId} />
      <div className="border border-border bg-card p-6 md:col-span-2">
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2">Scope summary</div>
        <Textarea
          defaultValue={e.scope_summary || ""}
          onBlur={(ev) => onSave({ scope_summary: ev.target.value })}
          rows={4}
          className="rounded-none bg-secondary/40"
          data-testid="engagement-scope-summary"
        />
      </div>
      <div className="border border-border bg-card p-6">
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-3">
          Objectives {(e.objectives || []).length > 0 && `(${e.objectives.length})`}
        </div>
        <StrategicList
          items={e.objectives || []}
          onSave={(next) => onSave({ objectives: next })}
          accent="objective"
          testidRoot="engagement-objective"
          addLabel="Add objective"
          empty="No objectives captured yet — add the outcomes this engagement is trying to move."
        />
      </div>
      <div className="border border-border bg-card p-6">
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-3">
          Key risks {(e.key_risks || []).length > 0 && `(${e.key_risks.length})`}
        </div>
        <StrategicList
          items={e.key_risks || []}
          onSave={(next) => onSave({ key_risks: next })}
          accent="risk"
          testidRoot="engagement-key-risk"
          addLabel="Add risk"
          empty="No risks captured yet — add what could derail this engagement."
        />
      </div>
    </div>
  );
}
